import { useCallback, useEffect, useState } from 'react';
import {
  api, ErreurApi,
  type ReponseAnomalies, type ReponseComparables, type ReponseSecteur,
  type ReponseTransactions, type Tendance, type TransactionAffichable,
} from './api';
import { Chiffre, Retenue, montantGNF } from './chiffre';

/**
 * §10 IMMOBILIER · §11 DVF — L'ÉCRAN DU MARCHÉ.
 *
 * ⚠️ CE QU'IL DOIT RENDRE IMPOSSIBLE : afficher une médiane sur trois ventes
 * avec la même allure qu'une médiane sur trois mille. Tous les nombres passent
 * par `<Chiffre>`, qui porte leur nature et leur effectif. En dessous des
 * seuils, le service rend « indéterminé » et l'écran l'affiche comme tel — en
 * ambre, avec son motif — plutôt qu'un zéro rassurant.
 *
 * ⚠️ ET « AUCUNE ANOMALIE » N'EST PAS « JE NE SAIS PAS ». Les deux ont leur
 * bandeau, leur couleur et leur phrase. Sur la page où un investisseur décide
 * s'il achète, les confondre coûte de l'argent réel.
 */

type Onglet = 'secteur' | 'ventes' | 'tendance' | 'anomalies' | 'comparables';

const ONGLETS: Array<{ cle: Onglet; libelle: string }> = [
  { cle: 'secteur', libelle: 'Analyse de secteur' },
  { cle: 'ventes', libelle: 'Transactions' },
  { cle: 'tendance', libelle: 'Évolution' },
  { cle: 'anomalies', libelle: 'Écarts de prix' },
  { cle: 'comparables', libelle: 'Comparables' },
];

const NATURES: Array<{ v: string; l: string }> = [
  { v: 'toutes', l: 'Toutes natures' },
  { v: 'vente', l: 'Ventes' },
  { v: 'echange', l: 'Échanges' },
  { v: 'donation', l: 'Donations' },
  { v: 'succession', l: 'Successions' },
];

export function Immobilier({ onOuvrirParcelle }: { onOuvrirParcelle: (id: string) => void }) {
  const [onglet, setOnglet] = useState<Onglet>('secteur');
  const [nature, setNature] = useState('toutes');
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [parcelle, setParcelle] = useState('');
  const [granularite, setGranularite] = useState('trimestre');

  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);
  const [secteur, setSecteur] = useState<ReponseSecteur | null>(null);
  const [ventes, setVentes] = useState<ReponseTransactions | null>(null);
  const [tendance, setTendance] = useState<Tendance | null>(null);
  const [anomalies, setAnomalies] = useState<ReponseAnomalies | null>(null);
  const [comp, setComp] = useState<ReponseComparables | null>(null);

  const parametres = useCallback(() => {
    const q = new URLSearchParams();
    if (nature !== 'toutes') q.set('nature', nature);
    if (du) q.set('du', du);
    if (au) q.set('au', au);
    if (parcelle.trim()) q.set('parcelle', parcelle.trim());
    return q;
  }, [nature, du, au, parcelle]);

  const charger = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      const q = parametres();
      if (onglet === 'secteur') { q.set('granularite', granularite); setSecteur(await api.immobilier.secteur(q)); }
      if (onglet === 'ventes') setVentes(await api.immobilier.transactions(q));
      if (onglet === 'tendance') { q.set('granularite', granularite); setTendance((await api.immobilier.tendances(q)).tendance); }
      if (onglet === 'anomalies') setAnomalies(await api.immobilier.anomalies(q));
      if (onglet === 'comparables') {
        const c = new URLSearchParams();
        if (parcelle.trim()) c.set('parcelle', parcelle.trim());
        setComp(await api.immobilier.comparables(c));
      }
    } catch (e) {
      // ⚠️ Un 403 de sensibilité n'est PAS une panne : c'est un palier. Le
      // service explique où aller ; on affiche SON message, pas le nôtre.
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    } finally { setChargement(false); }
  }, [onglet, parametres, granularite, parcelle]);

  useEffect(() => { void charger(); }, [charger]);

  return (
    <div className="tableau-bord">
      <div className="entete-scene">
        <h2>🏠 Marché immobilier</h2>
        <p className="mention">
          Le registre ne connaît que les transactions QU'IL A ENREGISTRÉES : une vente conclue
          sans mutation lui reste invisible. Chaque nombre porte sa nature — observé, calculé,
          estimé ou projeté — et rend « indéterminé » plutôt que zéro quand il ne peut pas conclure.
        </p>
      </div>

      <div className="filtres">
        <label>Parcelle
          <input value={parcelle} onChange={(e) => setParcelle(e.target.value)} placeholder="GN-PAR-…" />
        </label>
        <label>Nature
          <select value={nature} onChange={(e) => setNature(e.target.value)}>
            {NATURES.map((n) => <option key={n.v} value={n.v}>{n.l}</option>)}
          </select>
        </label>
        <label>Du <input type="date" value={du} onChange={(e) => setDu(e.target.value)} /></label>
        <label>Au <input type="date" value={au} onChange={(e) => setAu(e.target.value)} /></label>
        <label>Période
          <select value={granularite} onChange={(e) => setGranularite(e.target.value)}>
            <option value="mois">Mensuelle</option>
            <option value="trimestre">Trimestrielle</option>
            <option value="annee">Annuelle</option>
          </select>
        </label>
      </div>

      <div className="onglets-marche">
        {ONGLETS.map((o) => (
          <button key={o.cle} className={onglet === o.cle ? 'actif' : ''} onClick={() => setOnglet(o.cle)}>
            {o.libelle}
          </button>
        ))}
      </div>

      {erreur && <p className="erreur">{erreur}</p>}
      {chargement && <p className="attente">Interrogation du registre…</p>}

      {!chargement && !erreur && onglet === 'secteur' && secteur && (
        <>
          <h3 className="titre-section">{secteur.secteur.nom}</h3>
          <Retenue total={secteur.statistiques.effectif_total} retenu={secteur.statistiques.effectif_retenu}
                   ecartees={secteur.statistiques.ecartees} />
          <Statistiques s={secteur.statistiques} />
          <h3 className="titre-section">Évolution</h3>
          <Serie t={secteur.tendance} />
          <h3 className="titre-section">Écarts de prix</h3>
          {secteur.anomalies.anomalies === undefined && secteur.anomalies.champs_masques?.includes('anomalies')
            ? <p className="masque">Le détail des ventes atypiques demande une habilitation de niveau 2. Le diagnostic global reste affiché ci-dessous.</p>
            : null}
          <Diagnostic a={secteur.anomalies as ReponseAnomalies} />
        </>
      )}

      {!chargement && !erreur && onglet === 'ventes' && ventes && (
        <>
          {ventes.tronquee && (
            <p className="avertissement">
              Liste tronquée à {ventes.effectif} lignes. L'analyse porterait sur les ventes les plus
              récentes seulement — resserrez les filtres avant d'en tirer une conclusion.
            </p>
          )}
          <TableTransactions liste={ventes.transactions} onOuvrirParcelle={onOuvrirParcelle} />
        </>
      )}

      {!chargement && !erreur && onglet === 'tendance' && tendance && (
        <>
          <Serie t={tendance} />
          <div className="chiffres">
            <Chiffre libelle="Période suivante" v={tendance.projection_periode_suivante} unite="/m²"
                     format={(n) => montantGNF(n, null)} />
          </div>
          {tendance.date_estimee > 0 && (
            <p className="avertissement">
              {tendance.date_estimee} transaction(s) portent la date de leur ENREGISTREMENT et non celle
              de l'acte : la série est bruitée d'autant.
            </p>
          )}
        </>
      )}

      {!chargement && !erreur && onglet === 'anomalies' && anomalies && (
        <>
          <Diagnostic a={anomalies} />
          {anomalies.anomalies && anomalies.anomalies.length > 0 && (
            <table className="tableau">
              <thead><tr><th>Parcelle</th><th>Date</th><th>Prix/m²</th><th>Écart</th><th>Sens</th><th>Score</th></tr></thead>
              <tbody>
                {anomalies.anomalies.map((a) => (
                  <tr key={a.transaction.id}>
                    <td><button className="lien" onClick={() => onOuvrirParcelle(a.transaction.parcelle)}>{a.transaction.parcelle}</button></td>
                    <td>{a.transaction.dateTransaction ?? '—'}</td>
                    <td>{montantGNF(a.prixM2, null)}</td>
                    <td>{a.ecart_a_la_mediane_pct > 0 ? '+' : ''}{a.ecart_a_la_mediane_pct.toFixed(1)} %</td>
                    <td>{a.sens === 'sous_evaluee' ? 'sous-évaluée' : 'sur-évaluée'}</td>
                    <td>{a.score.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {!chargement && !erreur && onglet === 'comparables' && comp && (
        <>
          <p className="mention">
            Univers de comparaison : {comp.univers} transaction(s). Critères appliqués :{' '}
            {comp.criteres_appliques.join(' · ')}.
          </p>
          <div className="chiffres">
            <Chiffre libelle="Valeur estimée du bien" v={comp.valeur_estimee} format={(n) => montantGNF(n)} />
          </div>
          {comp.comparables.length === 0
            ? <p className="indetermine">Aucune vente comparable dans le périmètre. Ce n'est pas « le bien ne vaut rien » : c'est qu'il n'y a rien à quoi le comparer.</p>
            : (
              <table className="tableau">
                <thead><tr><th>Parcelle</th><th>Date</th><th>Prix/m²</th><th>Même division</th><th>Écart surface</th><th>Ancienneté</th></tr></thead>
                <tbody>
                  {comp.comparables.map((c) => (
                    <tr key={c.transaction.id}>
                      <td><button className="lien" onClick={() => onOuvrirParcelle(c.transaction.parcelle)}>{c.transaction.parcelle}</button></td>
                      <td>{c.transaction.dateTransaction ?? '—'}</td>
                      <td>{montantGNF(c.prixM2, null)}</td>
                      <td>{c.criteres.meme_division ? 'oui' : 'non'}</td>
                      <td>{c.criteres.ecart_surface_pct === null ? '—' : `${c.criteres.ecart_surface_pct.toFixed(0)} %`}</td>
                      <td>{c.criteres.anciennete_jours === null ? '—' : `${c.criteres.anciennete_jours} j`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </>
      )}
    </div>
  );
}

function Statistiques({ s }: { s: ReponseSecteur['statistiques'] }) {
  return (
    <div className="chiffres">
      <Chiffre libelle="Prix médian" v={s.prix_median} format={(n) => montantGNF(n, s.devise)} />
      <Chiffre libelle="Prix médian au m²" v={s.prix_m2_median} unite="/m²" format={(n) => montantGNF(n, null)} />
      <Chiffre libelle="1er quartile au m²" v={s.prix_m2_q1} unite="/m²" format={(n) => montantGNF(n, null)} />
      <Chiffre libelle="3e quartile au m²" v={s.prix_m2_q3} unite="/m²" format={(n) => montantGNF(n, null)} />
      <Chiffre libelle="Vente la moins chère" v={s.prix_m2_min} unite="/m²" format={(n) => montantGNF(n, null)} />
      <Chiffre libelle="Vente la plus chère" v={s.prix_m2_max} unite="/m²" format={(n) => montantGNF(n, null)} />
    </div>
  );
}

/**
 * ⚠️ LE DIAGNOSTIC A TROIS ÉTATS VISUELS, PAS DEUX. « je ne peux pas conclure »
 * (ambre), « rien à signaler » (vert) et « n écarts » (liste). Les deux
 * premiers valent tous deux zéro anomalie affichée et disent l'inverse l'un de
 * l'autre.
 */
function Diagnostic({ a }: { a: ReponseAnomalies }) {
  if (!a) return null;
  if (a.anomalies === null || a.anomalies === undefined) {
    return <p className="indetermine">⚠️ {a.lecture ?? a.diagnostic?.base}</p>;
  }
  if (a.anomalies.length === 0) {
    return (
      <p className="rien-a-signaler">
        Aucune vente ne s'écarte de la médiane locale au-delà du seuil,
        sur {a.diagnostic.observations} vente(s) examinée(s). Cette fois, le zéro est mesuré.
      </p>
    );
  }
  return <p className="avertissement">{a.lecture}</p>;
}

/** L'évolution, en barres. Une période creuse est HACHURÉE, jamais absente. */
function Serie({ t }: { t: Tendance }) {
  const valeurs = t.points.map((p) => p.prix_m2_median.valeur).filter((v): v is number => v !== null);
  if (t.points.length === 0) {
    return <p className="indetermine">Aucune période exploitable : le registre ne porte pas de vente datée dans ce périmètre.</p>;
  }
  const max = valeurs.length ? Math.max(...valeurs) : 1;
  return (
    <>
      <div className="serie">
        {t.points.map((p) => (
          <div key={p.periode} style={{ flex: 1 }}>
            <div className={`serie-barre ${p.prix_m2_median.valeur === null ? 'creuse' : ''}`}
                 style={{ height: p.prix_m2_median.valeur === null ? '18px' : `${Math.max(6, (p.prix_m2_median.valeur / max) * 88)}px` }}
                 title={p.prix_m2_median.valeur === null ? p.prix_m2_median.base
                        : `${montantGNF(p.prix_m2_median.valeur, null)}/m² · ${p.effectif} vente(s)`} />
            <div className="serie-etiquette">{p.periode}</div>
          </div>
        ))}
      </div>
      <p className="mention">
        Les périodes hachurées comptent trop peu de ventes pour une médiane — elles ne valent pas zéro,
        elles ne se mesurent pas. {t.sans_date > 0 && `${t.sans_date} transaction(s) sans date exploitable sont hors série.`}
      </p>
    </>
  );
}

function TableTransactions({ liste, onOuvrirParcelle }: {
  liste: TransactionAffichable[]; onOuvrirParcelle: (id: string) => void;
}) {
  if (liste.length === 0) {
    return <p className="indetermine">Aucune transaction ne correspond à ces filtres. Le registre n'en connaît pas — ce qui ne veut pas dire qu'il n'y en a pas eu.</p>;
  }
  return (
    <table className="tableau">
      <thead><tr><th>Parcelle</th><th>Nature</th><th>Date</th><th>Montant</th><th>Surface d'alors</th><th>Prix/m²</th><th>Mutation</th></tr></thead>
      <tbody>
        {liste.map((t) => (
          <tr key={t.id}>
            <td>
              <button className="lien" onClick={() => onOuvrirParcelle(t.parcelle)}>{t.parcelle}</button>
              {t.parcelleCourante === false && <em className="pastille" title="La parcelle a été divisée, fusionnée ou remplacée depuis">version retirée</em>}
            </td>
            <td>{t.nature}</td>
            <td>
              {t.dateTransaction ?? '—'}
              {t.dateEstimee && <em className="pastille" title="Date de l'enregistrement : la date de l'acte n'a pas été déclarée">enreg.</em>}
            </td>
            <td>{t.montant === null
              ? <span className="masque" title={t.nature === 'vente' ? "Lacune : une vente a toujours eu un prix" : "Pas de prix par nature"}>
                  {t.nature === 'vente' || t.nature === 'echange' ? 'non déclaré' : 'sans objet'}
                </span>
              : montantGNF(t.montant, t.devise)}</td>
            <td>{t.surfaceReferenceM2 === null ? '—' : `${t.surfaceReferenceM2.toLocaleString('fr-FR')} m²`}</td>
            <td>{t.prix_m2 == null ? '—' : montantGNF(t.prix_m2, null)}</td>
            <td>{t.mutation ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
